import { Box, Button, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { EasTooltip } from 'ui/EasTooltip';
import {
	createOrderDiscountLoader,
	createOrderReturnLoader,
	getConfirmedOrders,
} from 'redux/reducers/orders/selectors';
import {
	getConfirmedOrdersRequest,
	getOrderDetailsCombinedRequest,
} from 'redux/reducers/orders/reducer';
import { DateTime } from 'luxon';
import { ORDERS_STATUS } from 'redux/reducers/orders/constants';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { NewCorrectionDialog } from 'components/VatCorrections/CreateCorrectionButton/NewCorrectionDialog';
import queryString from 'query-string';
import {
	SCHEMES_TO_VAT_CORRECTIONS_SCHEMES,
	VAT_CORRECTIONS_SCHEMES,
} from 'redux/reducers/vatCorrections/constants';
import { getSavedUrl } from 'redux/reducers/auth/selectors';
import { VAT_CORRECTION_CREATION_DATE } from 'services/constants/env';
import { PATHS } from 'routes/constants';
import { OrderDetails } from './OrderDetails';
import { DeliveryInformation } from './DeliveryInformation';
import { AmountBreakdown } from './AmountBreakdown';
import { OrderReturns } from './OrderReturns';
import { OrderDiscounts } from './OrderDiscounts';
import { CreateReturnDialog } from './CreateReturnDialog';
import { CreateDiscountDialog } from './CreateDiscountDialog';
import { OrderVatCorrections } from './OrderVatCorrections';
import { OrderError } from './OrderError';
import { EasChip } from 'ui/EasChip';
import { ESalesTypes } from 'services/constants/representationSchemeType';

export const Order: FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const params = useParams<{ orderId?: string }>();
	const { orderId } = params;
	const orders = useSelector(getConfirmedOrders);
	const createOrderReturnIsLoading = useSelector(createOrderReturnLoader);
	const createOrderDiscountLoading = useSelector(createOrderDiscountLoader);
	const savedUrl = useSelector(getSavedUrl);
	const [openCreateReturnDialog, setOpenCreateReturnDialog] = useState(false);
	const handleOpenCreateReturnDialog = () => setOpenCreateReturnDialog(true);
	const handleCloseCreateReturnDialog = () => setOpenCreateReturnDialog(false);

	const [openCreateDiscountDialog, setOpenCreateDiscountDialog] = useState(false);
	const handleOpenCreateDiscountDialog = () => setOpenCreateDiscountDialog(true);
	const handleCloseCreateDiscountDialog = () => setOpenCreateDiscountDialog(false);

	const [openCreateCorrectionDialog, setOpenCreateCorrectionDialog] = useState(false);
	const handleCreateCorrection = () => {
		setOpenCreateCorrectionDialog(true);
		const schemes = orders?.data[0].schemes.split(',').map((scheme: string) => {
			if (!scheme) return null;
			if (scheme === 'IOSS-UNION') return 'OSS';
			if (scheme === 'IOSS-IMPORT') return 'IOSS';
			if (scheme === 'UK-IMPORT') return 'NonEU';
			if (scheme === 'NON_EU_DOMESTIC') return 'NonUnion';

			return null;
		});
		const schemeObj = schemes?.reduce((acc: any, scheme: any) => {
			if (!scheme) return acc;
			return { ...acc, [scheme]: true };
		}, {});

		const search = {
			...schemeObj,
			orderId: orders?.data[0]?.id,
		};

		navigate({
			pathname: location.pathname,
			search: queryString.stringify(search),
		});
	};
	const handleCloseCreateCorrection = () => setOpenCreateCorrectionDialog(false);

	useEffect(() => {
		if (!orderId) return;
		dispatch(getOrderDetailsCombinedRequest({ orderId }));
		dispatch(
			getConfirmedOrdersRequest({
				filters: [{ fieldName: 'id', value: orderId, operator: '=' }],
			}),
		);
	}, [dispatch, orderId]);

	const handleReplayHistory = () => {
		if (savedUrl) {
			if (savedUrl.includes(PATHS.ORDERS_PAGE)) navigate(savedUrl);
			else navigate(PATHS.ORDERS_PAGE);
		}
	};

	return (
		<Box>
			<Box sx={{ padding: '10px 20px 0 20px', maxWidth: '100vw', overflowX: 'auto' }}>
				<Box sx={{ minWidth: '660px' }}>
					{orders?.data.length === 0 && (
						<OrderError text="Order not found" linkText="Go to the Orders list" link="/orders" />
					)}

					{!!orders && !!orders.data.length && (
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
							<Box sx={{ mb: 2 }}>
								<Box
									sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 1 }}
								>
									<EasTooltip title="Go to the Orders list" arrow>
										<IconButton onClick={handleReplayHistory} color="primary">
											<KeyboardBackspaceIcon />
										</IconButton>
									</EasTooltip>
									<Typography variant="h5" color="primary">
										{orders?.data[0]?.externalOrderId}
									</Typography>
									{orders?.data[0]?.salesType === ESalesTypes.B2B ? (
										<EasChip label="B2B" bgcolor="warning.light" />
									) : (
										<EasChip label="B2C" bgcolor="primary.light" />
									)}
									{orders?.data[0]?.status && (
										<EasChip
											label={orders?.data[0]?.status?.replace('_', ' ')}
											bgcolor={
												orders?.data[0]?.status === ORDERS_STATUS.PAID
													? 'success.light'
													: orders?.data[0]?.status === ORDERS_STATUS.AWAITING_PAYMENT
														? 'warning.light'
														: 'primary.light'
											}
										/>
									)}
								</Box>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'start',
										gap: '20px',
									}}
								>
									{orders?.data?.[0]?.saleDateProvidedByEm && (
										<Typography
											variant="body2"
											gutterBottom
											color="GrayText"
											// sx={{ marginLeft: '50px' }}
										>
											Payment date:{' '}
											{DateTime.fromISO(orders?.data?.[0]?.saleDateProvidedByEm).toFormat(
												'yyyy-MM-dd, HH:mm',
											)}
										</Typography>
									)}
									{orders?.data?.[0]?.reportingPaymentDate && (
										<Typography
											variant="body2"
											gutterBottom
											color="GrayText"
											// sx={{ marginLeft: '50px' }}
										>
											Reporting date:{' '}
											{DateTime.fromISO(orders?.data?.[0]?.reportingPaymentDate).toFormat(
												'yyyy-MM-dd, HH:mm',
											)}
										</Typography>
									)}
								</Box>
							</Box>
							<Box
								sx={{
									mt: 1,
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									'&': {
										'& button': {
											borderRadius: '12px',
											textTransform: 'none',
											// padding: '2px 10px',
											fontSize: '.7rem',
										},
									},
								}}
							>
								<EasTooltip
									title={`Due to the fiscal report generation process, creating of the VAT correction will be available after 3rd of ${DateTime.now().toFormat('MMMM')}`}
									disableHoverListener={
										DateTime.now().toFormat('yyyy-MM-dd') >= VAT_CORRECTION_CREATION_DATE
									}
									timeout={
										DateTime.now().toFormat('yyyy-MM-dd') >= VAT_CORRECTION_CREATION_DATE
											? null
											: 3000
									}
									type="warning"
									arrow
								>
									<span>
										<Button
											disabled={
												DateTime.now().toFormat('yyyy-MM-dd') < VAT_CORRECTION_CREATION_DATE ||
												!orders?.data[0]?.schemes
													.split(',')
													.some((element) =>
														VAT_CORRECTIONS_SCHEMES.includes(
															SCHEMES_TO_VAT_CORRECTIONS_SCHEMES[
																element as unknown as keyof typeof SCHEMES_TO_VAT_CORRECTIONS_SCHEMES
															],
														),
													)
											}
											variant="contained"
											color="primary"
											onClick={handleCreateCorrection}
										>
											Create VAT Correction
										</Button>
									</span>
								</EasTooltip>

								{/* {orders?.data[0].schemes.split(',').map((scheme: string) => {
									if (!scheme) return null;
									if (scheme === 'IOSS-UNION')
										return (
											<Button
												variant="contained"
												color="primary"
												size="small"
												onClick={() => {
													handleCreateCorrection('OSS');
												}}
											>
												Create negative OSS correction
											</Button>
										);
									if (scheme === 'IOSS-IMPORT')
										return (
											<Button
												variant="contained"
												color="primary"
												size="small"
												onClick={() => {
													handleCreateCorrection('IOSS');
												}}
											>
												Create negative IOSS correction
											</Button>
										);
									return null;
								})} */}

								<Box sx={{ position: 'relative' }}>
									<Button
										variant="contained"
										color="primary"
										onClick={handleOpenCreateReturnDialog}
										disabled={
											createOrderReturnIsLoading ||
											createOrderDiscountLoading ||
											orders?.data[0]?.status === ORDERS_STATUS.AWAITING_PAYMENT
										}
									>
										Create Return
									</Button>
									{createOrderReturnIsLoading && (
										<CircularProgress
											size={24}
											sx={{
												color: '#004366',
												position: 'absolute',
												top: '50%',
												left: '50%',
												marginTop: '-12px',
												marginLeft: '-12px',
											}}
										/>
									)}
								</Box>
								<Box sx={{ position: 'relative' }}>
									<Button
										variant="contained"
										color="primary"
										onClick={handleOpenCreateDiscountDialog}
										disabled={
											createOrderReturnIsLoading ||
											createOrderDiscountLoading ||
											orders?.data[0]?.status === ORDERS_STATUS.AWAITING_PAYMENT
										}
									>
										Create Discount
									</Button>
									{createOrderDiscountLoading && (
										<CircularProgress
											size={24}
											sx={{
												color: '#004366',
												position: 'absolute',
												top: '50%',
												left: '50%',
												marginTop: '-12px',
												marginLeft: '-12px',
											}}
										/>
									)}
								</Box>
							</Box>
						</Box>
					)}
					<Grid container spacing={2}>
						<Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
							<OrderDetails />
							<OrderReturns />
							<OrderDiscounts />
							<OrderVatCorrections />
						</Grid>

						<Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
							<DeliveryInformation />
							<AmountBreakdown />
						</Grid>
					</Grid>
				</Box>
			</Box>
			<CreateReturnDialog
				open={openCreateReturnDialog}
				setClose={handleCloseCreateReturnDialog}
				orderId={orderId}
			/>
			<CreateDiscountDialog
				open={openCreateDiscountDialog}
				setClose={handleCloseCreateDiscountDialog}
				orderId={orderId}
			/>
			<NewCorrectionDialog
				open={openCreateCorrectionDialog}
				setClose={handleCloseCreateCorrection}
			/>
		</Box>
	);
};
